import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import GenericFormEditor from "../../components/form/genericFormEditor";
import TransferLineItemsComp from "../../components/warehouseTransfer/transferLineItems.comp";
import PosLayout from "../../components/layouts/posLayout.comp";
import {
  createWarehouseTransferNote,
  formatWarehouseTransferApiMessage,
  parsePosApiError,
} from "../../core/warehouseTransferNote.core";
import { useTranslation } from "../../core/translation.core";
import useGetRetailShopId from "../../hooks/useGetRetailShopId.hook";
import useRetailShop from "../../hooks/useRetailShop";
import {
  NewWarehouseTransferFromWarehouseSelect,
  WarehouseTransferToRetailShopSelect,
  WarehouseTransferToWarehouseSelect,
} from "../../components/warehouseTransfer/transferFieldSelects.comp";
import {
  type WarehouseTransferDetailFormValues,
  type WarehouseTransferFormMethods,
} from "../../hooks/warehouseTransfer/transferForm.shared";

export default function WarehouseTransferNewPage() {
  const { t, msg } = useTranslation();
  return (
    <PosLayout title={t(msg.warehouseTransfer.newTransfer)} showback>
      <Content />
    </PosLayout>
  );
}

function Content() {
  const { t, msg } = useTranslation();
  const router = useRouter();
  const queryClient = useQueryClient();
  const retailId = useGetRetailShopId();
  const currentRetail = useRetailShop();

  const methods = useForm<WarehouseTransferDetailFormValues>({
    defaultValues: {
      fromWarehouseId: "",
      toRetailShopId: "",
      toWarehouseId: "",
      lines: [],
    },
  });

  const toRetailShopIdWatched = methods.watch("toRetailShopId");

  useEffect(() => {
    methods.setValue("toWarehouseId", "");
  }, [methods, toRetailShopIdWatched]);

  const createMutation = useMutation({
    mutationFn: async (data: WarehouseTransferDetailFormValues) => {
      if (!data.fromWarehouseId || !data.toRetailShopId || !data.toWarehouseId || !data.lines?.length) {
        throw new Error(`Fetch Failed with url '': ${JSON.stringify({ message: t(msg.warehouseTransfer.noLineItems) })}`);
      }
      try {
        const created = await createWarehouseTransferNote({
          fromWarehouseId: data.fromWarehouseId,
          toRetailShopId: data.toRetailShopId,
          toWarehouseId: data.toWarehouseId,
          items: data.lines.map((l) => ({ productId: l.productId, quantity: l.quantity })),
        });
        await queryClient.invalidateQueries({ queryKey: ["warehouseTransferList"] });
        toast.success(t(msg.warehouseTransfer.saved));
        await router.push(`/warehouseTransfer/${String(created.id)}`);
      } catch (e) {
        const parsed = parsePosApiError(e);
        const message = formatWarehouseTransferApiMessage(parsed.message, t);
        throw new Error(`Fetch Failed with url '': ${JSON.stringify({ message })}`);
      }
    },
  });

  if (!retailId || !currentRetail) {
    return <p className="p-4 text-sm opacity-70">{t(msg.form.loading)}</p>;
  }

  const selectPlaceholder = t(msg.form.select);

  return (
    <main className="dark:text-white space-y-6 p-2 lg:p-4 max-w-3xl" data-role="warehouse-transfer-new-page">
      <h1 className="text-2xl font-bold">{t(msg.warehouseTransfer.newTransfer)}</h1>

      <GenericFormEditor<WarehouseTransferDetailFormValues>
        methods={methods as WarehouseTransferFormMethods}
        mutation={createMutation}
        btnText={t(msg.form.save)}
        cancelPath="/warehouseTransfer"
      >
        <div className="flex flex-col gap-4">
          <NewWarehouseTransferFromWarehouseSelect
            methods={methods as WarehouseTransferFormMethods}
            emptyOptionLabel={selectPlaceholder}
          />
          <WarehouseTransferToRetailShopSelect
            methods={methods as WarehouseTransferFormMethods}
            emptyOptionLabel={selectPlaceholder}
            defaultRetailShopId={retailId}
          />
          <WarehouseTransferToWarehouseSelect
            showLineEditor
            methods={methods as WarehouseTransferFormMethods}
            emptyOptionLabel={selectPlaceholder}
            lockUntilRetailSelected
          />
        </div>

        <Controller
          name="lines"
          control={methods.control}
          render={({ field }) => (
            <TransferLineItemsComp
              lines={field.value ?? []}
              onLinesChange={field.onChange}
              disabled={false}
              fromWarehouseId={methods.watch("fromWarehouseId")}
              toWarehouseId={methods.watch("toWarehouseId")}
            />
          )}
        />
      </GenericFormEditor>
    </main>
  );
}
